import React, { useState, useCallback, memo, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styles from './styles.module.scss'
import { PATH_URL, PATH_API } from 'src/helpers'
import { useAppContext } from 'src/helpers/app'
import PaletteItem from './PaletteItem'
import { useCollectionsColorPalettes } from './useCollectionsColorPalettes.hook'
import { getHttp } from 'src/api'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import ProgressBar from '../ProgressBar'

/**
 * ColorPalette Component
 * Starting page for tier2 flow that allows users to select a color palette for their flower arrangement
 * @returns {JSX.Element | null} The ColorPalette component or null if not mounted
 */
const ColorPalette: React.FC = () => {
  const history = useHistory()
  const { sessionID } = useAppContext()
  const [selectedPalette, setSelectedPalette] = useState<number | null>(null)
  const { palettes, loading, saveColorPaletteSelection } = useCollectionsColorPalettes()
  const [isChecking, setIsChecking] = useState(false)

  // Check for collectionId parameter and redirect if present
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in ColorPalette, redirecting to EmailEntryPage')
      // Navigate preserving all URL parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_EMAIL_ENTRY)
      history.replace(nextUrl)
    }
  }, [history])

  const handlePaletteClick = useCallback((index: number): void => {
    setSelectedPalette(index)
  }, [])

  const handleNext = useCallback(async () => {
    if (selectedPalette !== null && palettes[selectedPalette]) {
      setIsChecking(true)
      const selectedPaletteData = palettes[selectedPalette]

      try {
        // Save to localStorage
        localStorage.setItem('selectedPaletteId', selectedPaletteData.id)
        localStorage.setItem('selectedPalette', JSON.stringify(selectedPaletteData))
        const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
        const selectedPaletteFromStorage = JSON.parse(localStorage.getItem('selectedPalette') || '{}')
        collectionsQuizEntry.selectedPalette = selectedPaletteFromStorage
        collectionsQuizEntry.sessionID = sessionID
        localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
        // Save to server via API
        await saveColorPaletteSelection(selectedPaletteData.id)
        // Get and save list of images for this palette
        const res = await getHttp(PATH_API.getQuizSessionData)
        const allOptions = res.data.imagePreferenceOptions || []
        const filtered = allOptions.filter((img: any) => img.colorPaletteId == selectedPaletteData.id)
        localStorage.setItem(`flowerImagesForPalette_${selectedPaletteData.id}`, JSON.stringify(filtered))
        // Preload 9 images of selected palette
        filtered.slice(0, 9).forEach((img: any) => {
          if (img.imageUrl) {
            const image = new window.Image()
            image.src = img.imageUrl
          }
        })

        // Navigate to next page preserving URL parameters
        const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_FLOWER_SELECTION)
        history.push(nextUrl)
      } catch (err) {
        console.warn('Failed to save palette data:', err)
      } finally {
        setIsChecking(false)
      }
    }
  }, [history, palettes, selectedPalette, saveColorPaletteSelection, sessionID])

  if (loading || !palettes.length) return null

  return (
    <>
      <ProgressBar />
      <section className={styles.colorPalette} aria-labelledby="color-palette-title">
        <div className={styles.textContent}>
          <div className={styles.textContentContainer}>
            <h1 id="color-palette-title" className={styles.title}>
              Which color palette is speaking to you?
            </h1>
            <p className={styles.subtitle}>Choose one to get started, but you can change it later!</p>
            <button
              className={`${styles.continueButton} ${selectedPalette !== null ? styles.validSelection : ''}`}
              onClick={handleNext}
              disabled={selectedPalette === null || isChecking}
              aria-disabled={selectedPalette === null || isChecking}
            >
              CONTINUE
            </button>
          </div>
        </div>
        <div className={styles.grid} role="grid" aria-label="Color palette selection grid">
          {palettes.map((palette, index) => {
            const imageUrl =
              palette.image || palette.overviewCarousels?.[0]?.imageUrl || palette.images?.desktop?.[0]?.url || ''
            return (
              <PaletteItem
                key={`palette-${palette.id}`}
                palette={palette}
                index={index}
                isSelected={selectedPalette === index}
                onSelect={handlePaletteClick}
                imageUrl={imageUrl}
              />
            )
          })}
        </div>

        <div className={styles.continueMobileContainer}>
          <button
            className={`${styles.continueButtonMobile} ${selectedPalette !== null ? styles.validSelection : ''}`}
            onClick={handleNext}
            disabled={selectedPalette === null || isChecking}
            aria-disabled={selectedPalette === null || isChecking}
          >
            CONTINUE
          </button>
        </div>
      </section>
    </>
  )
}

export default memo(ColorPalette)
